import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { OPTIONS, SUCCESS_90D_MAX } from "@/lib/onboardingOptions";
import ChipGrid from "./ChipGrid";

const transition = { duration: 0.5, ease: [0.22, 1, 0.36, 1] };

export default function Step2StageGoal({ draft, setDraft, next, prev }) {
  const set = (f) => (v) => setDraft((d) => ({ ...d, [f]: v }));
  const canContinue =
    draft.startup_stage && draft.main_goal && draft.target_timeline;

  return (
    <motion.div
      data-testid="onboarding-step-2"
      key="step-2"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={transition}
      className="text-white"
    >
      <h2 className="text-[28px] font-extrabold leading-[1.1] tracking-tight md:text-[32px]">
        Where are you today?
      </h2>
      <p className="mt-2 text-[13px] text-white/55">
        Stage · goal · timeline — so the boardroom knows what "winning" means.
      </p>

      <div className="mt-6 space-y-6">
        <Group label="What stage is your startup at?">
          <ChipGrid
            testIdPrefix="q-startup-stage"
            options={OPTIONS.startup_stage}
            value={draft.startup_stage}
            onChange={set("startup_stage")}
            columns={2}
          />
        </Group>

        <Group label="What is your main goal right now?">
          <ChipGrid
            testIdPrefix="q-main-goal"
            options={OPTIONS.main_goal}
            value={draft.main_goal}
            onChange={set("main_goal")}
            columns={3}
          />
        </Group>

        <Group label="Target timeline for meaningful results?">
          <ChipGrid
            testIdPrefix="q-target-timeline"
            options={OPTIONS.target_timeline}
            value={draft.target_timeline}
            onChange={set("target_timeline")}
            columns={3}
          />
        </Group>

        <Group label={`What would success look like in the next 90 days? (pick up to ${SUCCESS_90D_MAX}, optional)`}>
          <ChipGrid
            testIdPrefix="q-success-90d"
            options={OPTIONS.success_90d}
            value={draft.success_90d || []}
            onChange={set("success_90d")}
            multi
            max={SUCCESS_90D_MAX}
            columns={2}
          />
        </Group>
      </div>

      <footer className="mt-8 flex items-center justify-between">
        <button
          type="button"
          data-testid="onboarding-step-back"
          onClick={prev}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white/60 hover:text-white"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.4} />
          Back
        </button>
        <button
          type="button"
          data-testid="onboarding-step-continue"
          disabled={!canContinue}
          onClick={next}
          className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-[13px] font-bold text-black shadow-[0_10px_30px_-10px_rgba(255,255,255,0.5)] transition-opacity disabled:opacity-40"
        >
          Continue
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
        </button>
      </footer>
    </motion.div>
  );
}

function Group({ label, children }) {
  return (
    <div>
      <div className="eyebrow mb-2 text-white/55">{label}</div>
      {children}
    </div>
  );
}
